import React, {useEffect, useState, useCallback} from 'react';
import {
    fetchPaymentMethods,
    addPaymentMethod,
    setDefaultCard,
    editCardName,
    fetchSubscriptionPrice,
    deletePaymentMethod,
    fetchSubscriptionInvoices
} from '../utils/requests/payments';
import {Icon} from '@iconify/react/dist/iconify.js';
import {useAtom} from 'jotai';
import {settingsAtom} from '../utils/atoms';
import {useLocation, useNavigate, Link} from 'react-router-dom';
import {fetchSmsBalance} from '../utils/requests/smsCredits';


const roundDecimal = (number, ceil) =>
    ceil ? Math.ceil(number * 100) / 100 : Math.round(number * 100) / 100


const BillingLayer = () => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addCardLoading, setAddCardLoading] = useState(false);
    const [formData, setFormData] = useState({name: '', phoneNumber: ''});
    const [settings] = useAtom(settingsAtom);
    const location = useLocation();
    const navigate = useNavigate();
    const [hasHandledRedirect, setHasHandledRedirect] = useState(false);
    const [settingDefault, setSettingDefault] = useState(null);
    const [editingCard, setEditingCard] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [deletingCard, setDeletingCard] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [smsBalance, setSmsBalance] = useState(null);

    const loadCards = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchPaymentMethods();
            setCards(data);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadSubscriptionInfo = useCallback(async () => {
        try {
            const data = await fetchSubscriptionPrice();
            setSubscriptionInfo(data);
        } catch (err) {
            console.error('Failed to load subscription info:', err);
        }
    }, []);

    const loadInvoices = useCallback(async () => {
        try {
            const data = await fetchSubscriptionInvoices();
            data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setInvoices(data);
        } catch (err) {
            console.error('Failed to load invoices:', err);
        }
    }, []);

    const loadSmsBalance = useCallback(async () => {
        try {
            const data = await fetchSmsBalance();
            setSmsBalance(data);
        } catch (err) {
            console.error('Failed to load SMS balance:', err);
        }
    }, []);

    useEffect(() => {
        loadCards();
        loadSubscriptionInfo();
        loadInvoices();
        loadSmsBalance();
    }, [loadCards, loadSubscriptionInfo, loadInvoices, loadSmsBalance]);

    // Handle URL parameters for success/failure
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const success = urlParams.get('success');

        if (success !== null && !hasHandledRedirect) {
            setHasHandledRedirect(true);

            // Reload cards to check for changes
            loadCards();

            // Clear the URL parameter
            navigate('/billing', {replace: true});

            // Show success/error message
            if (success === 'true') {
                // You could add a toast notification here
                console.log('Card added successfully');
            } else {
                console.log('Failed to add card');
            }
        }
    }, [location.search, navigate, loadCards, hasHandledRedirect]);

    // Reset the redirect flag when location changes
    useEffect(() => {
        setHasHandledRedirect(false);
    }, [location.pathname]);

    // Pre-fill form with user data when modal opens
    useEffect(() => {
        if (showAddModal && settings?.teacher) {
            const teacherName = `${settings.teacher.firstName || ''} ${settings.teacher.lastName || ''}`.trim();
            const teacherPhone = settings.teacher.phoneNumber || '';

            setFormData({
                name: teacherName,
                phoneNumber: teacherPhone,
            });
        }
    }, [showAddModal, settings]);

    const handleAddCard = async (e) => {
        e.preventDefault();
        setAddCardLoading(true);

        try {
            const response = await addPaymentMethod(formData.name, formData.phoneNumber);
            if (response.success && response.url) {
                // Navigate to the credit card form URL
                window.location.href = response.url;
            }
        } catch (error) {
            console.error('Failed to add card:', error);
            setError('Failed to add card');
        } finally {
            setAddCardLoading(false);
        }
    };

    const handleSetDefault = async (cardId) => {
        setSettingDefault(cardId);
        try {
            await setDefaultCard(cardId);
            // Reload cards to reflect the change
            await loadCards();
        } catch (error) {
            console.error('Failed to set default card:', error);
            setError('Failed to set default card');
        } finally {
            setSettingDefault(null);
        }
    };

    const handleStartEdit = (card) => {
        setEditingCard(card.id);
        setEditingName(card.name);
    };

    const handleSaveEdit = async (cardId) => {
        if (!editingName.trim()) return;

        try {
            await editCardName(cardId, editingName.trim());
            // Reload cards to reflect the change
            await loadCards();
            setEditingCard(null);
            setEditingName('');
        } catch (error) {
            console.error('Failed to edit card name:', error);
            setError('Failed to edit card name');
        }
    };

    const handleCancelEdit = () => {
        setEditingCard(null);
        setEditingName('');
    };

    const handleDeleteCard = async (cardId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק כרטיס זה?')) {
            return;
        }

        setDeletingCard(cardId);
        try {
            await deletePaymentMethod(cardId);
            // Reload cards to reflect the change
            await loadCards();
        } catch (error) {
            console.error('Failed to delete card:', error);
            setError('Failed to delete card');
        } finally {
            setDeletingCard(null);
        }
    };

    if (loading) return <div>טוען כרטיסים...</div>;
    if (error) return <div>שגיאה בטעינת כרטיסים</div>;

    return (
        <>
            <div className="col-12">
                <div className="radius-8 border-0">
                    <div>
                        {/* Credit Cards Section */}
                        <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                            <h6 className="mb-2fw-bold text-lg mt-20 ">ניהול כרטיסי אשראי</h6>
                        </div>

                        <div>
                            {cards.length === 0 ? (
                                <div className="row g-4">
                                    <div className="col-md-6 col-lg-4">
                                        <div
                                            className="card border-0 shadow-sm h-100 position-relative overflow-hidden d-flex align-items-center justify-content-center"
                                            style={{borderRadius: '16px', minHeight: '200px', cursor: 'pointer'}}
                                            onClick={() => setShowAddModal(true)}>
                                            <div className="text-center p-4">
                                                <div className="mb-3">
                                                    <Icon icon="ph:plus-circle" className="icon text-primary"
                                                          style={{fontSize: '3rem'}}/>
                                                </div>
                                                <h6 className="text-primary fw-semibold mb-2">הוסף כרטיס אשראי</h6>
                                                <p className="text-muted small mb-0">לחץ כאן להוספת כרטיס חדש</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-4">
                                    {cards.map(card => (
                                        <div key={card.id} className="col-md-6 col-lg-4">
                                            <div
                                                className="card border-0 shadow-sm h-100 position-relative overflow-hidden"
                                                style={{borderRadius: '16px'}}>
                                                <div
                                                    className="card-body p-7 justify-content-between d-flex flex-column">
                                                    <div>
                                                        <div
                                                            className="d-flex align-items-center justify-content-between mb-3">
                                                            <div
                                                                className="d-flex align-items-center gap-2 flex-grow-1">
                                                                {editingCard === card.id ? (
                                                                    <div
                                                                        className="d-flex align-items-center gap-2 flex-grow-1">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control form-control-sm border-0 bg-light"
                                                                            value={editingName}
                                                                            onChange={(e) => setEditingName(e.target.value)}
                                                                            onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(card.id)}
                                                                            autoFocus
                                                                            style={{borderRadius: '8px'}}
                                                                        />
                                                                        <button
                                                                            className="btn btn-sm btn-success rounded-circle d-flex align-items-center justify-content-center p-0 w-auto"
                                                                            onClick={() => handleSaveEdit(card.id)}
                                                                            style={{
                                                                                width: 32,
                                                                                height: 32,
                                                                                aspectRatio: 1
                                                                            }}
                                                                        >
                                                                            <Icon icon="ph:check" className="icon"/>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center p-0 w-auto"
                                                                            onClick={handleCancelEdit}
                                                                            style={{
                                                                                width: 32, height: 32, aspectRatio: 1
                                                                            }}
                                                                        >
                                                                            <Icon icon="ph:x" className="icon"/>
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <h6 className="mb-0 text-primary flex-grow-1 d-flex gap-2 align-items-center fw-semibold">
                                                                            {card.name}
                                                                            {!!card.isDefault && (
                                                                                <span
                                                                                    className="badge bg-success text-sm px-2 py-4"
                                                                                    style={{fontSize: '0.7rem'}}>ראשי</span>
                                                                            )}
                                                                        </h6>
                                                                        <button
                                                                            className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center p-0"
                                                                            onClick={() => handleStartEdit(card)}
                                                                            style={{width: '32px', height: '32px'}}
                                                                        >
                                                                            <Icon icon="ph:pencil"
                                                                                  className="icon text-muted"/>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="mb-3">
                                                            <div className="fw-bold text-dark"
                                                                 style={{fontSize: '1.1rem', letterSpacing: '1px'}}>
                                                                {card.lastDigits} **** **** ****
                                                            </div>
                                                        </div>

                                                    </div>
                                                    <div
                                                        className="d-flex justify-content-end align-items-center justify-self-end">
                                                        <div className="d-flex gap-2">
                                                            {!card.isDefault && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary rounded-pill  d-flex align-items-center justify-content-center"
                                                                    onClick={() => handleSetDefault(card.id)}
                                                                    disabled={settingDefault === card.id}
                                                                    style={{fontSize: '0.8rem', padding: '6px 12px'}}
                                                                >
                                                                    {settingDefault === card.id ? (
                                                                        <Icon icon="ph:spinner" className="icon spin"/>
                                                                    ) : (
                                                                        'הגדר כראשי'
                                                                    )}
                                                                </button>
                                                            )}
                                                            {!card.isDefault && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center p-0"
                                                                    style={{width: '32px', height: '32px'}}
                                                                    onClick={() => handleDeleteCard(card.id)}
                                                                    disabled={deletingCard === card.id}
                                                                >
                                                                    {deletingCard === card.id ? (
                                                                        <Icon icon="ph:spinner" className="icon spin"/>
                                                                    ) : (
                                                                        <Icon icon="ph:trash" className="icon"/>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add Card Button as a Card */}
                                    <div className="col-md-6 col-lg-4">
                                        <div
                                            className="card border-0 shadow-sm position-relative overflow-hidden d-flex align-items-center justify-content-center"
                                            style={{borderRadius: '16px', minHeight: '200px', cursor: 'pointer'}}
                                            onClick={() => setShowAddModal(true)}>
                                            <div className="text-center p-4">
                                                <div className="mb-3">
                                                    <Icon icon="ph:plus-circle" className="icon text-primary"
                                                          style={{fontSize: '3rem'}}/>
                                                </div>
                                                <h6 className="text-primary fw-semibold mb-2">הוסף כרטיס אשראי</h6>
                                                <p className="text-muted small mb-0">לחץ כאן להוספת כרטיס חדש</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Subscription Info */}
                        <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20 mt-20">
                            <h6 className="mb-2fw-bold text-lg mt-20 ">מנוי</h6>
                        </div>
                        {subscriptionInfo && subscriptionInfo.plans && subscriptionInfo.plans.length > 0 && (
                            <div className="row gy-4 mb-4">
                                <div className="col-xxl-3 col-xl-4 col-sm-6">
                                    <div className="card p-3 shadow-2 radius-8 h-100 bg-gradient-end-primary">
                                        <div className="card-body p-0">
                                            <div
                                                className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="mb-0 w-48-px h-48-px bg-primary-100 text-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                                                        <Icon icon="ph:credit-card" className="icon"/>
                                                    </span>
                                                    <div>
                                                        <h6 className="fw-semibold mb-2">₪{roundDecimal(subscriptionInfo.nextChargePriceIncludeTax)}</h6>
                                                        <span className="fw-medium text-secondary-light text-sm">
                                                            החיוב הבא
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            {subscriptionInfo.nextChargePriceIncludeTax !== subscriptionInfo.priceIncludeTax ? (
                                                <p className="text-sm mb-0">
                                                    מחיר בסיס: ₪{roundDecimal(subscriptionInfo.price)}
                                                </p>
                                            ) : (<p className="text-sm mb-0">
                                                לפני מע״מ: ₪{roundDecimal(subscriptionInfo.nextChargePrice)}
                                            </p>)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-3 col-xl-4 col-sm-6">
                                    <div className="card p-3 shadow-2 radius-8 h-100 bg-gradient-end-success">
                                        <div className="card-body p-0">
                                            <div
                                                className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="mb-0 w-48-px h-48-px bg-success-100 text-success-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                                                        <Icon icon="ph:calendar" className="icon"/>
                                                    </span>
                                                    <div>
                                                        <h6 className="fw-semibold mb-2">
                                                            {new Date(subscriptionInfo.nextChargeDate).toLocaleDateString('he-IL')}
                                                        </h6>
                                                        <span className="fw-medium text-secondary-light text-sm">
                                                            תשלום הבא
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            {cards.length > 0 ? (

                                                <p className="text-sm mb-0">
                                                    יחוייב מהאשראי אוטומטית
                                                </p>
                                            ) : <p className="text-sm mb-0">
                                                הוסף אשראי כדי להבטיח פעילות רציפה
                                            </p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SMS Credits Section */}
                        <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20 mt-20">
                            <h6 className="mb-2 fw-bold text-lg mt-20">קרדיטים SMS</h6>
                        </div>
                        <div className="row gy-4 mb-4">
                            <div className="col-xxl-3 col-xl-4 col-sm-6">
                                <div className="card p-3 shadow-2 radius-8 h-100 bg-gradient-end-info">
                                    <div className="card-body p-0">
                                        <div className="d-flex align-items-center gap-2 mb-8">
                                            <span className="mb-0 w-48-px h-48-px bg-info-100 text-info-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                                                <Icon icon="ph:chat-circle-text" className="icon"/>
                                            </span>
                                            <div>
                                                <h6 className="fw-semibold mb-2">{smsBalance?.totalCredits ?? '...'}</h6>
                                                <span className="fw-medium text-secondary-light text-sm">סה״כ קרדיטים זמינים</span>
                                            </div>
                                        </div>
                                        <p className="text-sm mb-0">
                                            {smsBalance ? `${smsBalance.freeCredits} חינם + ${smsBalance.purchasedCredits} שנרכשו` : ''}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xxl-3 col-xl-4 col-sm-6 d-flex align-items-center">
                                <Link to="/sms-credits" className="btn btn-primary d-flex align-items-center gap-2">
                                    <Icon icon="ph:shopping-cart" className="icon"/>
                                    רכישת קרדיטים SMS
                                </Link>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            {/* Recent Invoices Section */}
            <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20 mt-20">
                <h6 className="mb-2 fw-bold text-lg mt-20">חשבוניות אחרונות</h6>
            </div>

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-0">
                    {invoices.length === 0 ? (
                        <div className="text-center py-4">
                            <Icon icon="ph:receipt" className="icon text-muted mb-3" style={{fontSize: '3rem'}}/>
                            <p className="text-muted mb-0">אין חשבוניות להצגה</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th className="border-0 fw-semibold">תאריך</th>
                                    <th className="border-0 fw-semibold">כותרת</th>
                                    <th className="border-0 fw-semibold">גודל הקובץ</th>
                                    <th className="border-0 fw-semibold text-left"></th>
                                </tr>
                                </thead>
                                <tbody>
                                {invoices.slice(0, 10).map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td className="align-middle">
                                            {new Date(invoice.date).toLocaleDateString('he-IL', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',

                                            })}
                                        </td>
                                        <td className="align-middle">
                                                <span className="text-muted">
                                                    {invoice.notes}
                                                </span>
                                        </td>
                                        <td className="align-middle">
                                                <span className="badge bg-light text-dark">
                                                    {invoice.size}
                                                </span>
                                        </td>
                                        <td className="align-middle text-left">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => window.open(invoice.url, '_blank')}
                                                title="צפה בחשבונית"
                                            >
                                                <Icon icon="ph:download" className="icon inline-block mb-2"/>
                                                <span className="mx-4">
                                                        הורדה
                                                    </span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            {/* Add Card Modal */}
            {showAddModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{display: 'block'}} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">הוסף כרטיס אשראי</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowAddModal(false)}
                                    ></button>
                                </div>
                                <form onSubmit={handleAddCard}>
                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label htmlFor="cardName" className="form-label">שם מלא</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="cardName"
                                                value={formData.name}
                                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="cardPhone" className="form-label">מספר טלפון</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                id="cardPhone"
                                                value={formData.phoneNumber}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    phoneNumber: e.target.value
                                                })}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowAddModal(false)}
                                        >
                                            ביטול
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={addCardLoading}
                                        >
                                            {addCardLoading ? 'טוען...' : 'המשך'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default BillingLayer; 